import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';

// Survives React StrictMode remounts — only one popup can be open at a time across all posts
const _openPopup = { postId: null, type: null };

const PRIVACY_OPTIONS = [
    {
        value: 0,
        label: 'Public',
        icon: '🌍',
        desc: 'Everyone can see this post',
    },
    {
        value: 1,
        label: 'Only Me',
        icon: '🔒',
        desc: 'Only you can see this post',
    },
    {
        value: 2,
        label: 'Friends Only',
        icon: '👥',
        desc: 'Only your friends can see this post',
    },
];

const toLocalDatetimeInput = (utcStr) => {
    if (!utcStr) return '';
    const ts = utcStr.includes('Z') || utcStr.includes('+') ? utcStr : utcStr.replace(' ', 'T') + 'Z';
    const d = new Date(ts);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const FavoriteButton = ({ post, auth, GetPoost, allPostIds, onPrivacyChange, onDateChange }) => {
    const [favAnimating, setFavAnimating] = useState(false);
    const [favorited, setFavorited] = useState(post.is_fav);
    const [favId, setFavId] = useState(post.is_fav ? post.is_fav.id : null);
    const postId = post.post_data.id;

    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [showPrivacyPopup, _setShowPrivacyPopup] = useState(
        () => _openPopup.postId === postId && _openPopup.type === 'privacy'
    );
    const [privacy, setPrivacy] = useState(post.post_data.privacy ?? 0);
    const [privacyLoading, setPrivacyLoading] = useState(false);
    const [showDatePopup, _setShowDatePopup] = useState(
        () => _openPopup.postId === postId && _openPopup.type === 'date'
    );
    const [dateValue, setDateValue] = useState('');
    const [dateLoading, setDateLoading] = useState(false);

    const setShowPrivacyPopup = (val) => {
        _setShowPrivacyPopup(val);
        if (val) { _openPopup.postId = postId; _openPopup.type = 'privacy'; }
        else if (_openPopup.postId === postId && _openPopup.type === 'privacy') { _openPopup.postId = null; _openPopup.type = null; }
    };
    const setShowDatePopup = (val) => {
        _setShowDatePopup(val);
        if (val) { _openPopup.postId = postId; _openPopup.type = 'date'; }
        else if (_openPopup.postId === postId && _openPopup.type === 'date') { _openPopup.postId = null; _openPopup.type = null; }
    };

    const dropdownRef = useRef(null);
    const anyPopupOpen = useRef(false);
    const isMyPost = auth.id === post.post_data.user.id;

    useEffect(() => {
        return () => {
            if (_openPopup.postId === postId) { _openPopup.postId = null; _openPopup.type = null; }
        };
    }, []);

    const handleFavorite = async () => {
        setFavAnimating(true);
        try {
            if (favorited) {
                if (!favId) return;
                const response = await axios.post('https://buzzinguniverse.com/backend/api/disfav-request', {
                    id: favId,
                    post_id: post.post_data.id,
                    user_id: auth.id
                });
                if (response.data.code === 200) {
                    setFavorited(false);
                    setFavId(null);
                }
            } else {
                const response = await axios.post('https://buzzinguniverse.com/backend/api/fav-request', {
                    post_id: post.post_data.id,
                    user_id: auth.id
                });
                if (response.data.code === 200) {
                    setFavorited(true);
                    setFavId(response.data.data.id);
                }
            }
        } catch (error) {
            console.error("Error favoriting/unfavoriting post:", error);
            alert(error.response?.data?.message || "Failed to process request");
        } finally {
            setTimeout(() => setFavAnimating(false), 500);
            GetPoost && GetPoost();
        }
    };

    const handleDelete = async (e) => {
        try {
            const container = e.target.closest('.personal_des_box');
            const idsToDelete = allPostIds && allPostIds.length > 0 ? allPostIds : [post.post_data.id];
            const confirmDelete = window.confirm("Are you sure you want to delete this post?");
            if (!confirmDelete) return;

            const deletePromises = idsToDelete.map(id =>
                axios.post('https://buzzinguniverse.com/backend/api/delete/post', {
                    post_id: id,
                    user_id: auth.id
                })
            );

            const results = await Promise.all(deletePromises);
            const allSucceeded = results.every(res => res.data.code === 200);

            if (allSucceeded) {
                if (container) container.remove();
                GetPoost && GetPoost();
            } else {
                alert("Some images failed to delete");
            }
        } catch (error) {
            console.error("Error deleting post:", error);
            alert(error.response?.data?.message || "Failed to delete post");
        }
    };

    const openDatePopup = () => {
        setDateValue(toLocalDatetimeInput(post.post_data.created_at));
        setDropdownVisible(false);
        setShowDatePopup(true);
    };

    const updateDate = async () => {
        if (!dateValue) return;
        setDateLoading(true);
        try {
            const utcDate = new Date(dateValue).toISOString().slice(0, 19).replace('T', ' ');
            const res = await axios.post('https://buzzinguniverse.com/backend/api/update-post-date', {
                user_id: auth.id,
                post_id: post.post_data.id,
                created_at: utcDate,
            });
            if (res.data.code === 200) {
                onDateChange && onDateChange(post.post_data.id, utcDate);
                setShowDatePopup(false);
                GetPoost && GetPoost();
            }
        } catch (error) {
            console.error('Date update failed:', error);
        } finally {
            setDateLoading(false);
        }
    };

    const updatePrivacy = async (newPrivacy) => {
        setPrivacyLoading(true);
        try {
            await axios.post('https://buzzinguniverse.com/backend/api/update-post-privacy', {
                user_id: auth.id,
                post_id: post.post_data.id,
                privacy: newPrivacy,
            });
            setPrivacy(newPrivacy);
            onPrivacyChange && onPrivacyChange(newPrivacy);
            setShowPrivacyPopup(false);
        } catch (error) {
            console.error('Privacy update failed:', error);
        } finally {
            setPrivacyLoading(false);
        }
    };

    // anyPopupOpen ref sync karo — jab bhi privacy ya date popup open ho
    useEffect(() => {
        anyPopupOpen.current = showPrivacyPopup || showDatePopup;
    }, [showPrivacyPopup, showDatePopup]);

    const handleOutsideClick = useCallback((e) => {
        // Popup open hai toh mousedown bilkul ignore karo
        if (anyPopupOpen.current) return;
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setDropdownVisible(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [handleOutsideClick]);

    return (
        <>
            <div className="fav-btn-dropdown" ref={dropdownRef}>
                <button
                    className="fav-btn-dropdown-toggle"
                    onClick={() => setDropdownVisible(!dropdownVisible)}
                >
                    <i className="fas fa-ellipsis-v"></i>
                </button>

                {dropdownVisible && (
                    <div className="fav-btn-dropdown-menu">
                        <button
                            onClick={() => { handleFavorite(); setDropdownVisible(false); }}
                            className="fav-btn-dropdown-item"
                        >
                            <i className={`fas fa-star me-2 ${favorited ? 'text-warning' : ''}`}></i>
                            {favorited ? 'Unmark as Favorite' : 'Mark as Favorite'}
                        </button>
                        {isMyPost && (
                            <button
                                onClick={() => { setDropdownVisible(false); setShowPrivacyPopup(true); }}
                                className="fav-btn-dropdown-item"
                            >
                                <i className="fas fa-lock me-2"></i>
                                Privacy
                                <span className="privacy-current-badge">
                                    {PRIVACY_OPTIONS.find(o => o.value === privacy)?.icon}
                                </span>
                            </button>
                        )}
                        {isMyPost && (
                            <button
                                onClick={openDatePopup}
                                className="fav-btn-dropdown-item"
                            >
                                <i className="fas fa-calendar-alt me-2"></i>
                                Edit Date
                            </button>
                        )}
                        {isMyPost && (
                            <button
                                onClick={(e) => { handleDelete(e); setDropdownVisible(false); }}
                                className="fav-btn-dropdown-item fav-btn-dropdown-item--danger"
                            >
                                <i className="fas fa-trash me-2"></i>
                                Delete
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Privacy Popup */}
            {showPrivacyPopup && createPortal(
                <div className="privacy-popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowPrivacyPopup(false); }}>
                    <div className="privacy-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="privacy-popup-header">
                            <h3>Post Privacy</h3>
                            <button className="privacy-popup-close" onClick={() => setShowPrivacyPopup(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <p className="privacy-popup-subtitle">Who can see this post?</p>
                        <div className="privacy-options-list">
                            {PRIVACY_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`privacy-option-card ${privacy === opt.value ? 'active' : ''} ${privacyLoading ? 'disabled' : ''}`}
                                    onClick={() => !privacyLoading && updatePrivacy(opt.value)}
                                    disabled={privacyLoading}
                                >
                                    <span className="privacy-option-icon">{opt.icon}</span>
                                    <div className="privacy-option-text">
                                        <strong>{opt.label}</strong>
                                        <span>{opt.desc}</span>
                                    </div>
                                    {privacy === opt.value && (
                                        <span className="privacy-option-check">
                                            {privacyLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
            {showDatePopup && createPortal(
                <div className="privacy-popup-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowDatePopup(false); }}>
                    <div className="privacy-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="privacy-popup-header">
                            <h3>Edit Post Date</h3>
                            <button className="privacy-popup-close" onClick={() => setShowDatePopup(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <p className="privacy-popup-subtitle"></p>
                        <div style={{ padding: '16px 24px 24px' }}>
                            <input
                                type="datetime-local"
                                value={dateValue}
                                onChange={(e) => setDateValue(e.target.value)}
                                max={toLocalDatetimeInput(new Date().toISOString())}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6',
                                    fontSize: '15px',
                                    marginBottom: '16px',
                                }}
                            />
                            <button
                                onClick={updateDate}
                                disabled={dateLoading || !dateValue}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    background: dateLoading ? '#aaa' : '#4a90e2',
                                    color: '#fff',
                                    border: 'none',
                                    fontSize: '15px',
                                    cursor: dateLoading ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {dateLoading ? <i className="fas fa-spinner fa-spin me-2"></i> : <i className="fas fa-check me-2"></i>}
                                Save
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default FavoriteButton;
